import { useState } from 'react'
import { useNavigate } from 'react-router'
import type { ColumnDef, ColumnFiltersState } from '@tanstack/react-table'
import { formatDistanceToNow } from 'date-fns'
import { useAdminUserList } from '@/hooks/useAdminUsers'
import type { AdminUser } from '@/lib/api/admin-users'
import { DataTable } from '@/components/shared/DataTable'
import { DataTableColumnHeader } from '@/components/shared/DataTableColumnHeader'
import { DataTableFacetedFilter } from '@/components/shared/DataTableFacetedFilter'
import { PageHeader } from '@/components/shared/PageHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Search, X } from 'lucide-react'
import { adminTierLabel, isLegacyBusinessPreviewActive, normalizeLegacyTier } from '@/lib/tierLadder'

const tierColors: Record<string, string> = {
  trial: 'bg-muted text-muted-foreground',
  pilot: 'bg-blue-100 text-blue-800',
  pro: 'bg-purple-100 text-purple-800',
  business: 'bg-green-100 text-green-800',
  expired: 'bg-red-100 text-red-800',
}

const statusColors: Record<string, string> = {
  none: 'bg-muted text-muted-foreground',
  active: 'bg-green-100 text-green-800',
  past_due: 'bg-red-100 text-red-800',
  canceled: 'bg-yellow-100 text-yellow-800',
  trialing: 'bg-blue-100 text-blue-800',
  lifetime: 'bg-purple-100 text-purple-800',
}

const TRIAL_DURATION_MS = 3 * 24 * 60 * 60 * 1000

// Stable category used for sorting/filtering — unlike the badge label (which embeds a
// live countdown, e.g. "Trial — 2 days left"), this doesn't change from row to row for
// users in the same state, so it can be matched against a fixed set of filter options.
const STATUS_FILTER_OPTIONS = [
  { value: 'trial_active', label: 'Trial active' },
  { value: 'trial_expired', label: 'Trial expired' },
  { value: 'trial_not_started', label: 'Trial not started' },
  { value: 'active', label: 'Active' },
  { value: 'past_due', label: 'Past due' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'trialing', label: 'Trialing (Stripe)' },
  { value: 'lifetime', label: 'Lifetime' },
  { value: 'none', label: 'None' },
]

const TIER_FILTER_OPTIONS = [
  { value: 'trial', label: 'Trial' },
  { value: 'pilot', label: 'Pilot' },
  { value: 'pro', label: 'Pro' },
  { value: 'business', label: 'Business' },
  { value: 'expired', label: 'Expired' },
]

const BILLING_FILTER_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
  { value: 'lifetime', label: 'Lifetime' },
  { value: 'none', label: '— (no plan)' },
]

function getStatusCategory(user: AdminUser): string {
  if (user.subscription_tier === 'expired') return 'trial_expired'
  if (user.subscription_tier === 'trial') {
    if (!user.trial_started_at) return 'trial_not_started'
    const expiresAt = new Date(user.trial_started_at).getTime() + TRIAL_DURATION_MS
    if (Date.now() > expiresAt) return 'trial_expired'
    return 'trial_active'
  }
  return user.subscription_status
}

/** Trial clock starts on the user's first save-bearing editor event (item placed or
 * layout saved), not at registration — a Friday-night signup who returns Monday still
 * gets a full 72-hour window. See docs/product/pricing/tier-model.md
 * §"Trial clock starts on first editor session". Item caps (5 items/1 location/3 layouts)
 * apply to trial users from signup regardless of whether this clock has started — it only
 * governs when the nightly sweep flips the tier to `expired`. */
function getStatusTooltip(user: AdminUser): string | null {
  if (user.subscription_tier === 'trial') {
    if (!user.trial_started_at) {
      return 'Trial clock not started yet — begins on first item placed or layout saved. Item caps already apply.'
    }
    const started = new Date(user.trial_started_at)
    const expires = new Date(started.getTime() + TRIAL_DURATION_MS)
    return `Trial clock started ${started.toLocaleString()} — expires ${expires.toLocaleString()}`
  }
  if (user.subscription_tier === 'expired' && user.trial_started_at) {
    const started = new Date(user.trial_started_at)
    const expires = new Date(started.getTime() + TRIAL_DURATION_MS)
    return `Trial clock started ${started.toLocaleString()} — expired ${expires.toLocaleString()}`
  }
  return null
}

function getStatusBadge(user: AdminUser): { label: string; className: string } {
  const category = getStatusCategory(user)
  if (category === 'trial_expired') {
    return { label: 'Trial expired', className: 'bg-red-100 text-red-800' }
  }
  if (category === 'trial_not_started') {
    return { label: 'Trial not started', className: 'bg-muted text-muted-foreground' }
  }
  if (category === 'trial_active') {
    const expiresAt = new Date(new Date(user.trial_started_at!).getTime() + TRIAL_DURATION_MS)
    return {
      label: `Trial — ${formatDistanceToNow(expiresAt)} left`,
      className: 'bg-yellow-100 text-yellow-800',
    }
  }
  return {
    label: user.subscription_status,
    className: statusColors[user.subscription_status] ?? 'bg-muted text-muted-foreground',
  }
}

const columns: ColumnDef<AdminUser, unknown>[] = [
  {
    accessorKey: 'display_name',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
    cell: ({ row }) => (
      <span className="flex items-center gap-2 font-medium">
        {row.original.display_name || '—'}
        {row.original.is_test && (
          <Badge variant="outline" className="border-amber-300 bg-amber-50 px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wide text-amber-800">
            Test
          </Badge>
        )}
      </span>
    ),
  },
  {
    accessorKey: 'email',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Email" />,
    cell: ({ getValue }) => (
      <span className="text-sm text-muted-foreground">{getValue() as string}</span>
    ),
  },
  {
    accessorKey: 'subscription_tier',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Tier" />,
    filterFn: 'arrIncludesSome',
    cell: ({ row, getValue }) => {
      const tier = getValue() as string
      // legacy_caps is an entitlement flag, not a tier value (it must not enter
      // subscription_tier, which Stripe round-trips) — so it is shown as a
      // suffix here, which is the only place an operator would look for it.
      const { legacy_caps, legacy_business_preview_until } = row.original
      const previewOpen = isLegacyBusinessPreviewActive(
        normalizeLegacyTier(tier), legacy_caps, legacy_business_preview_until,
      )
      return (
        <span className="flex items-center gap-1">
          <Badge variant="secondary" className={tierColors[tier]}>
            {adminTierLabel(tier, legacy_caps)}
          </Badge>
          {previewOpen && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="outline" className="cursor-default border-green-300 bg-green-50 px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wide text-green-800">
                  Biz preview
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                Business features open until {new Date(legacy_business_preview_until!).toLocaleDateString()}
              </TooltipContent>
            </Tooltip>
          )}
        </span>
      )
    },
  },
  {
    id: 'status',
    accessorFn: (row) => getStatusCategory(row),
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    filterFn: 'arrIncludesSome',
    cell: ({ row }) => {
      const { label, className } = getStatusBadge(row.original)
      const tooltip = getStatusTooltip(row.original)
      const badge = (
        <Badge variant="secondary" className={className}>
          {label}
        </Badge>
      )
      if (!tooltip) return badge
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-default">{badge}</span>
          </TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      )
    },
  },
  {
    id: 'billing',
    accessorFn: (row) => row.billing_cycle ?? 'none',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Billing" />,
    filterFn: 'arrIncludesSome',
    cell: ({ getValue }) => {
      const cycle = getValue() as string
      return <span className="text-sm text-muted-foreground">{cycle === 'none' ? '—' : cycle}</span>
    },
  },
  {
    accessorKey: 'layouts_count',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Layouts" />,
    cell: ({ getValue }) => getValue() as number,
  },
  {
    accessorKey: 'locations_count',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Locations" />,
    cell: ({ getValue }) => getValue() as number,
  },
  {
    accessorKey: 'created_at',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Signed Up" />,
    cell: ({ getValue }) => {
      const date = getValue() as string
      return (
        <span className="text-sm text-muted-foreground">
          {formatDistanceToNow(new Date(date), { addSuffix: true })}
        </span>
      )
    },
  },
  {
    accessorKey: 'last_sign_in_at',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Last Active" />,
    sortingFn: (a, b) => {
      const av = a.original.last_sign_in_at ? new Date(a.original.last_sign_in_at).getTime() : 0
      const bv = b.original.last_sign_in_at ? new Date(b.original.last_sign_in_at).getTime() : 0
      return av - bv
    },
    cell: ({ getValue }) => {
      const date = getValue() as string | null
      if (!date) return <span className="text-sm text-muted-foreground">Never</span>
      return (
        <span className="text-sm text-muted-foreground">
          {formatDistanceToNow(new Date(date), { addSuffix: true })}
        </span>
      )
    },
  },
]

export function UserListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [includeTestUsers, setIncludeTestUsers] = useState(false)
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

  // Load the full list (capped at 100 by the edge function) and paginate + sort
  // client-side — server-side pagination defeats client-side sorting otherwise.
  const { data, isLoading } = useAdminUserList({ pageSize: 100, search, includeTestUsers })

  return (
    <div>
      <PageHeader title="Users" description="Manage user accounts and subscriptions" />

      <DataTable
        columns={columns}
        data={data?.users ?? []}
        isLoading={isLoading}
        onRowClick={(user) => navigate(`/users/${user.id}`)}
        emptyMessage="No users found."
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        toolbar={(table) => (
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  setPage(1)
                }}
                className="pl-9"
              />
            </div>
            <DataTableFacetedFilter column={table.getColumn('subscription_tier')} title="Tier" options={TIER_FILTER_OPTIONS} />
            <DataTableFacetedFilter column={table.getColumn('status')} title="Status" options={STATUS_FILTER_OPTIONS} />
            <DataTableFacetedFilter column={table.getColumn('billing')} title="Billing" options={BILLING_FILTER_OPTIONS} />
            {columnFilters.length > 0 && (
              <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setColumnFilters([])}>
                Reset
                <X className="h-4 w-4" />
              </Button>
            )}
            <div className="ml-auto flex items-center gap-2">
              <Switch
                id="include-test-users"
                checked={includeTestUsers}
                onCheckedChange={(v) => {
                  setIncludeTestUsers(v)
                  setPage(1)
                }}
              />
              <Label htmlFor="include-test-users" className="cursor-pointer text-sm text-muted-foreground">
                Show test users
              </Label>
            </div>
          </div>
        )}
        pagination={{ pageIndex: page - 1, pageSize: 20 }}
        onPaginationChange={(updater) => {
          const next = typeof updater === 'function'
            ? updater({ pageIndex: page - 1, pageSize: 20 })
            : updater
          setPage(next.pageIndex + 1)
        }}
      />
    </div>
  )
}
